/**
 * Context builders for DOM observer data.
 * Transforms scanner results into context format for registration.
 */

import type { ChartsData } from './chart-scanners';
import type { PageActionsData, PageStructureData, TableContextData, TableInfo } from './types';

/**
 * Build context string for page structure data.
 *
 * @param pageStructure - Scanned page structure data
 * @returns JSON string representation, or null if no data
 */
export function buildPageStructureContext(pageStructure: PageStructureData | null): string | null {
  if (!pageStructure) {
    return null;
  }
  return JSON.stringify(pageStructure);
}

/**
 * Build context string for table selection data.
 *
 * @param tableData - Scanned table selection data
 * @returns JSON string representation, or null if no data
 */
export function buildTableSelectionContext(tableData: TableContextData | null): string | null {
  if (!tableData) {
    return null;
  }
  return JSON.stringify(tableData);
}

/**
 * Build context string for visible tables data.
 *
 * @param tables - Array of scanned table data
 * @returns JSON string representation, or null if no tables
 */
export function buildVisibleTablesContext(tables: TableInfo[]): string | null {
  if (tables.length === 0) {
    return null;
  }

  return JSON.stringify({
    tables,
    tableCount: tables.length,
  });
}

/**
 * Build context string for page actions data.
 *
 * @param actionsData - Scanned page actions data
 * @returns JSON string representation, or null if no data
 */
export function buildPageActionsContext(actionsData: PageActionsData | null): string | null {
  if (!actionsData) {
    return null;
  }
  return JSON.stringify(actionsData);
}

/**
 * Build context string for charts data.
 *
 * @param chartsData - Scanned charts data with full chart details
 * @returns JSON string representation, or null if no charts
 */
export function buildChartsContext(chartsData: ChartsData | null): string | null {
  if (!chartsData || chartsData.chartCount === 0) {
    return null;
  }
  return JSON.stringify(chartsData);
}
